import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useApiFetch } from "../api/apiClient";
import { colors } from "../utils/styles";
import { fmtDateTime } from "../utils/dateFormat";
import { compareValues, isEmpty } from "../utils/sort";
import { getSubmissionYear, yearOptionsFrom } from "../utils/submission";

// Report categories
const SUBMISSION_REPORTS = new Set([
  "all-subagency-submissions",
  "latest-subagency-submissions"
]);

const AGREEMENT_REPORTS = new Set([
  "all-signer-submissions",
  "latest-signer-submissions",
  "no-submission-authorized-signer",
  "authorized-signer-variance",
  "city-agreement-authorized-signers",
  "district-agreement-authorized-signers"
]);

// Subset of AGREEMENT_REPORTS that gets its own Year/Agency filters (the other
// two agreement reports have the separate Agreement_Name filter instead).
const SIGNER_REPORTS = new Set([
  "all-signer-submissions",
  "latest-signer-submissions",
  "no-submission-authorized-signer",
  "authorized-signer-variance"
]);

// Reports where the CSV export is restricted to rows the user has explicitly
// checked in the preview.
const FILTERABLE_REPORT_IDS = new Set([
  "city-agreement-authorized-signers",
  "district-agreement-authorized-signers"
]);

// These reports' Year filter can't be derived client-side like every other
// report's — either the rows carry no year column at all
// (no-submission-authorized-signer lists non-submitters), or picking a year
// has to change which submission counts as "latest" per signer/group
// (city-/district-agreement-authorized-signers). Both cases need the server
// to re-run the query, so they share the standalone fetch path below instead
// of yearFilters/signerYearFilters.
const YEAR_FILTER_REPORT_IDS = new Set([
  "no-submission-authorized-signer",
  "city-agreement-authorized-signers",
  "district-agreement-authorized-signers"
]);

const AGREEMENT_NAME_COLUMN = "Agreement_Name";

// Real Agreement_Name values start with an agency code, so this sentinel can't
// collide when used as a synthetic "Select all" option in the Autocomplete.
const SELECT_ALL_OPTION = "__SELECT_ALL__";

function stripCode(value) {
  if (!value) return value;
  const match = value.match(/^(.+?)\s+\([^)]+\)$/);
  return match ? match[1] : value;
}

export default function AdminReports() {
  const apiFetch = useApiFetch();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category'); // 'submission' or 'agreement'
  const [reports, setReports] = useState(null); // list of { id, title }
  const [reportsError, setReportsError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [activeTitle, setActiveTitle] = useState("");
  const [data, setData] = useState(null); // { columns, rows } or null
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  // For filterable reports: Set of row indices (into data.rows) that are checked.
  // null when the active report is not filterable.
  const [checkedRows, setCheckedRows] = useState(null);
  // null = no filter (all values pass); otherwise a concrete list of accepted values.
  const [selectedAgreementNames, setSelectedAgreementNames] = useState(null);
  // Submission report filters (Year, Agency/Group, Subagency)
  const [yearFilters, setYearFilters] = useState([]);
  const [agencyFilters, setAgencyFilters] = useState([]);
  const [subagencyFilters, setSubagencyFilters] = useState([]);
  // Signer/variance report filters (Year, Agency/Group) — kept separate from
  // the submission-report filters above so switching report types never
  // leaks a stale filter across.
  const [signerYearFilters, setSignerYearFilters] = useState([]);
  const [signerAgencyFilters, setSignerAgencyFilters] = useState([]);
  // Server-driven Year filter for YEAR_FILTER_REPORT_IDS only — see its comment.
  const [serverYear, setServerYear] = useState(null);
  const [serverYearOptions, setServerYearOptions] = useState([]);

  // Plain Set lookups — cheaper than the useMemo that would wrap them, and a
  // primitive boolean stays stable as a dependency of the memos below.
  const isFilterable = FILTERABLE_REPORT_IDS.has(activeId);
  const isSubmissionReport = SUBMISSION_REPORTS.has(activeId);
  const isSignerReport = SIGNER_REPORTS.has(activeId);

  const clearSubmissionFilters = useCallback(() => {
    setYearFilters([]);
    setAgencyFilters([]);
    setSubagencyFilters([]);
  }, []);

  const clearSignerFilters = useCallback(() => {
    setSignerYearFilters([]);
    setSignerAgencyFilters([]);
  }, []);

  // Re-fetches a YEAR_FILTER_REPORT_IDS report's data scoped to `year` (null =
  // no year filter, today's default behavior for that report). Unlike every
  // other filter here, this one has to hit the server.
  const loadYearFilteredData = useCallback(
    async (reportId, year) => {
      setDataError(null);
      setDataLoading(true);
      try {
        const qs = year ? `?year=${encodeURIComponent(year)}` : "";
        const res = await apiFetch(`/admin/reports/${reportId}/data${qs}`);
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(text || `HTTP ${res.status}`);
        }
        const payload = await res.json();
        setData(payload);
        // A year change swaps the underlying row set for checkbox-export
        // reports — re-check every row of the new payload, mirroring what
        // selectReport does on first load.
        if (FILTERABLE_REPORT_IDS.has(reportId)) {
          setCheckedRows(new Set(payload.rows.keys()));
        }
      } catch (err) {
        setDataError(err.message || "Failed to load report.");
      } finally {
        setDataLoading(false);
      }
    },
    [apiFetch]
  );

  // Load report list.
  useEffect(() => {
    let cancelled = false;
    apiFetch("/admin/reports")
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list = await res.json();
        if (!cancelled) setReports(list);
      })
      .catch((err) => {
        if (!cancelled) setReportsError(err.message || "Failed to load reports.");
      });
    return () => {
      cancelled = true;
    };
  }, [apiFetch]);

  const selectReport = useCallback(
    async (report) => {
      setActiveId(report.id);
      setActiveTitle(report.title);
      setData(null);
      setDataError(null);
      setDataLoading(true);
      setPage(0);
      setSortBy(null);
      setSortDir("asc");
      setCheckedRows(null);
      setSelectedAgreementNames(null);
      clearSubmissionFilters();
      clearSignerFilters();
      setServerYear(null);
      setServerYearOptions([]);
      if (YEAR_FILTER_REPORT_IDS.has(report.id)) {
        apiFetch(`/admin/reports/${report.id}/years`)
          .then((res) => (res.ok ? res.json() : []))
          .then(setServerYearOptions)
          .catch(() => setServerYearOptions([]));
      }
      try {
        const res = await apiFetch(`/admin/reports/${report.id}/data`);
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(text || `HTTP ${res.status}`);
        }
        const payload = await res.json();
        setData(payload);
        if (FILTERABLE_REPORT_IDS.has(report.id)) {
          setCheckedRows(new Set(payload.rows.keys()));
        }
      } catch (err) {
        setDataError(err.message || "Failed to load report.");
      } finally {
        setDataLoading(false);
      }
    },
    [apiFetch, clearSubmissionFilters, clearSignerFilters]
  );

  const agreementNameColumnIndex = useMemo(
    () => (data ? data.columns.indexOf(AGREEMENT_NAME_COLUMN) : -1),
    [data]
  );
  const allAgreementNames = useMemo(() => {
    if (!data || agreementNameColumnIndex < 0) return [];
    return Array.from(
      new Set(data.rows.map((r) => r[agreementNameColumnIndex]).filter((v) => !isEmpty(v)))
    ).sort((a, b) => String(a).localeCompare(String(b)));
  }, [data, agreementNameColumnIndex]);

  // For submission reports: find column indices. A missing column stays -1, and
  // row[-1] is undefined, so consumers can read straight through without guards.
  const submissionColumns = useMemo(() => {
    if (!data || !isSubmissionReport) return { year: -1, parentCode: -1, agency: -1, agencyGroup: -1, subagency: -1, subagencyCode: -1 };
    const idx = (name) => data.columns.indexOf(name);
    return {
      year: idx("SubmissionIdentifier"),
      parentCode: idx("PARENT_JURISDICTION_CODE"),
      agency: idx("PARENT_JURISDICTION_NAME"),
      agencyGroup: idx("GroupName"),
      subagency: idx("TaxBillDescriptionSubmission"),
      subagencyCode: idx("JurisdictionCode")
    };
  }, [data, isSubmissionReport]);


  // A submission is filed under a parent OR a group, never both, so the group
  // name wins when present. Shared by the option builders and the row filter.
  const rowAgencyName = useCallback(
    (row) => row[submissionColumns.agencyGroup] || row[submissionColumns.agency] || null,
    [submissionColumns]
  );

  // Build filter options for submission reports
  const yearOptions = useMemo(() => {
    if (!data || !isSubmissionReport) return [];
    return yearOptionsFrom(data.rows.map((row) => getSubmissionYear(row[submissionColumns.year])));
  }, [data, isSubmissionReport, submissionColumns.year]);

  const agencyOptions = useMemo(() => {
    if (!data || !isSubmissionReport) return [];
    const seen = new Set();
    data.rows.forEach(row => {
      const group = row[submissionColumns.agencyGroup];
      const name = rowAgencyName(row);
      // Groups have no parent code, so only parents get the "(CODE)" suffix.
      const code = group ? null : row[submissionColumns.parentCode];
      const agency = code ? `${name} (${code})` : name;
      if (agency) seen.add(agency);
    });
    return Array.from(seen).sort((a, b) => String(a).localeCompare(String(b)));
  }, [data, isSubmissionReport, submissionColumns.agencyGroup, submissionColumns.parentCode, rowAgencyName]);

  const subagencyOptions = useMemo(() => {
    if (!data || !isSubmissionReport || submissionColumns.subagency < 0) return [];
    const seen = new Set();
    const agencyNames = agencyFilters.length ? new Set(agencyFilters.map(stripCode)) : null;
    data.rows.forEach(row => {
      // Filter by selected agencies if any are selected
      if (agencyNames && !agencyNames.has(rowAgencyName(row))) return;
      const subagencyName = row[submissionColumns.subagency];
      const subagencyCode = row[submissionColumns.subagencyCode];
      const subagency = subagencyCode ? `${subagencyName} (${subagencyCode})` : subagencyName;
      if (subagency) seen.add(subagency);
    });
    return Array.from(seen).sort((a, b) => String(a).localeCompare(String(b)));
  }, [data, isSubmissionReport, submissionColumns.subagency, submissionColumns.subagencyCode, agencyFilters, rowAgencyName]);

  // Drops any subagency selections that no longer belong to the selected
  // agency set, without re-deriving eligibility separately from subagencyOptions.
  useEffect(() => {
    setSubagencyFilters((prev) => {
      if (prev.length === 0) return prev;
      const validSet = new Set(subagencyOptions);
      const next = prev.filter((s) => validSet.has(s));
      return next.length === prev.length ? prev : next;
    });
  }, [subagencyOptions]);

  // For signer/variance reports: these don't share one column-naming
  // convention (unlike the two submission reports above), so resolve indices
  // per report id rather than with one shared alias table.
  const signerColumns = useMemo(() => {
    const empty = { date: -1, agency: -1, group: -1, parentCode: -1 };
    if (!data || !isSignerReport) return empty;
    const idx = (name) => data.columns.indexOf(name);
    switch (activeId) {
      case "all-signer-submissions":
      case "latest-signer-submissions":
        return {
          date: idx("SubmissionIdentifier"),
          agency: idx("FUNDING_AGENCY"),
          group: idx("GROUP_NAME"),
          parentCode: idx("ParentJurisdictionCode")
        };
      case "no-submission-authorized-signer":
        return { date: -1, agency: idx("AgencyName"), group: -1, parentCode: idx("ParentJurisdictionCode") };
      case "authorized-signer-variance":
        return { date: idx("LatestSubmission"), agency: -1, group: -1, parentCode: idx("ParentJurisdictionCode") };
      default:
        return empty;
    }
  }, [data, isSignerReport, activeId]);

  // signerColumns.date resolves to a literal SubmissionIdentifier-shaped column
  // on every report that has one ("SubmissionIdentifier" itself, or
  // "LatestSubmission" on authorized-signer-variance, which is the same
  // SubmissionIdentifier value under an alias) — so this is the same
  // getSubmissionYear call the submission-report filters above already use.
  // "no-submission-authorized-signer" has no such column (date === -1), so
  // this always returns null there.
  const rowSignerYear = useCallback(
    (row) => (signerColumns.date >= 0 ? getSubmissionYear(row[signerColumns.date]) : null),
    [signerColumns]
  );

  // Composition differs per report: group-then-parent-with-code for the two
  // submission-shaped reports and no-submission-authorized-signer (mirrors
  // agencyOptions' XOR pattern above); bare ParentJurisdictionCode only for
  // authorized-signer-variance (no name column exists there).
  const rowSignerAgency = useCallback(
    (row) => {
      if (activeId === "authorized-signer-variance") {
        return row[signerColumns.parentCode] || null;
      }
      const group = signerColumns.group >= 0 ? row[signerColumns.group] : null;
      const name = group || row[signerColumns.agency] || null;
      if (!name) return null;
      const code = group ? null : row[signerColumns.parentCode];
      return code ? `${name} (${code})` : name;
    },
    [activeId, signerColumns]
  );

  const signerYearOptions = useMemo(() => {
    if (!data || !isSignerReport || signerColumns.date < 0) return [];
    return yearOptionsFrom(data.rows.map((row) => rowSignerYear(row)));
  }, [data, isSignerReport, signerColumns.date, rowSignerYear]);

  const signerAgencyOptions = useMemo(() => {
    if (!data || !isSignerReport) return [];
    const seen = new Set();
    data.rows.forEach((row) => {
      const label = rowSignerAgency(row);
      if (label) seen.add(label);
    });
    return Array.from(seen).sort((a, b) => String(a).localeCompare(String(b)));
  }, [data, isSignerReport, rowSignerAgency]);

  // null when no filter is active (all rows pass); otherwise a Set of accepted indices.
  const visibleRowIndices = useMemo(() => {
    if (!data) {
      return null;
    }
    // Check which filters are active
    const hasAgreementFilter = isFilterable && agreementNameColumnIndex >= 0 && selectedAgreementNames !== null;
    const hasSubmissionFilters =
      isSubmissionReport && (yearFilters.length > 0 || agencyFilters.length > 0 || subagencyFilters.length > 0);
    const hasSignerFilters =
      isSignerReport && (signerYearFilters.length > 0 || signerAgencyFilters.length > 0);
    if (!hasAgreementFilter && !hasSubmissionFilters && !hasSignerFilters) {
      return null;
    }
    const agreementSet = hasAgreementFilter ? new Set(selectedAgreementNames) : null;
    const yearSet = yearFilters.length ? new Set(yearFilters) : null;
    const agencySet = agencyFilters.length ? new Set(agencyFilters.map(stripCode)) : null;
    const subagencySet = subagencyFilters.length ? new Set(subagencyFilters.map(stripCode)) : null;
    const signerYearSet = signerYearFilters.length ? new Set(signerYearFilters) : null;
    const signerAgencySet = signerAgencyFilters.length ? new Set(signerAgencyFilters) : null;
    const result = new Set();
    data.rows.forEach((row, i) => {
      // Apply agreement filter
      if (agreementSet && !agreementSet.has(row[agreementNameColumnIndex])) return;
      // Apply submission report filters. Each Set is null unless a submission
      // report is active — selecting a report clears all three.
      if (yearSet && !yearSet.has(getSubmissionYear(row[submissionColumns.year]))) return;
      if (agencySet && !agencySet.has(rowAgencyName(row))) return;
      if (subagencySet && !subagencySet.has(row[submissionColumns.subagency])) return;
      // Apply signer/variance report filters. No stripCode needed — unlike
      // agencyFilters, signerAgencyOptions and rowSignerAgency both use the
      // same composition, so filter values already match row values directly.
      if (signerYearSet && !signerYearSet.has(rowSignerYear(row))) return;
      if (signerAgencySet && !signerAgencySet.has(rowSignerAgency(row))) return;
      result.add(i);
    });
    return result;
  }, [
    data,
    isFilterable,
    agreementNameColumnIndex,
    selectedAgreementNames,
    isSubmissionReport,
    yearFilters,
    agencyFilters,
    subagencyFilters,
    submissionColumns,
    rowAgencyName,
    isSignerReport,
    signerYearFilters,
    signerAgencyFilters,
    rowSignerYear,
    rowSignerAgency
  ]);

  const downloadCsv = useCallback(() => {
    if (!activeId || !data) return;
    try {
      // `checkedRows` can retain indices that are no longer visible after a
      // filter change — intersect with `visibleRowIndices` so those don't leak
      // back into the export.
      const rowsToExport =
        isFilterable && checkedRows
          ? data.rows.filter((_, i) => (!visibleRowIndices || visibleRowIndices.has(i)) && checkedRows.has(i))
          : data.rows;
      const blob = new Blob([buildCsv(data.columns, rowsToExport)], {
        type: "text/csv;charset=utf-8"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeId}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDataError(err.message || "Download failed.");
    }
  }, [activeId, data, isFilterable, checkedRows, visibleRowIndices]);

  // Entries are [row, originalIndex] so the checkbox column can toggle the
  // correct entry in `checkedRows` after sorting scrambles the display order.
  const sortedRows = useMemo(() => {
    if (!data) return [];
    const indexed = visibleRowIndices
      ? data.rows.reduce((acc, row, i) => {
          if (visibleRowIndices.has(i)) acc.push([row, i]);
          return acc;
        }, [])
      : data.rows.map((row, i) => [row, i]);
    if (sortBy === null) return indexed;
    const dir = sortDir === "asc" ? 1 : -1;
    return indexed.slice().sort(([a], [b]) => compareValues(a[sortBy], b[sortBy], dir));
  }, [data, sortBy, sortDir, visibleRowIndices]);
  // Clamp page so a filter change that shrinks the row set doesn't leave us
  // stuck on a now-empty page.
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const pagedRows = sortedRows.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  const exportableCount = useMemo(() => {
    if (!isFilterable || !checkedRows) return 0;
    return sortedRows.reduce((n, [, i]) => n + (checkedRows.has(i) ? 1 : 0), 0);
  }, [isFilterable, checkedRows, sortedRows]);
  const allVisibleChecked = isFilterable && sortedRows.length > 0 && exportableCount === sortedRows.length;
  const someVisibleChecked = isFilterable && exportableCount > 0 && exportableCount < sortedRows.length;

  const toggleRow = (originalIndex) => {
    setCheckedRows((prev) => {
      const next = new Set(prev);
      if (next.has(originalIndex)) next.delete(originalIndex);
      else next.add(originalIndex);
      return next;
    });
  };

  const toggleAll = () => {
    setCheckedRows((prev) => {
      const next = new Set(prev);
      const visibleIndices = sortedRows.map(([, i]) => i);
      const everyVisibleChecked = visibleIndices.every((i) => next.has(i));
      if (everyVisibleChecked) visibleIndices.forEach((i) => next.delete(i));
      else visibleIndices.forEach((i) => next.add(i));
      return next;
    });
  };

  // `null` selection = no filter (all rows pass). For the Autocomplete's
  // `value` prop we pass the full option list, so every option renders as
  // checked when unfiltered.
  const agreementFilterValue = selectedAgreementNames ?? allAgreementNames;
  const agreementFilterOptions = useMemo(
    () => [SELECT_ALL_OPTION, ...allAgreementNames],
    [allAgreementNames]
  );
  const agreementAllSelected = selectedAgreementNames === null;
  const agreementSomeSelected = selectedAgreementNames !== null && selectedAgreementNames.length > 0;

  // Filter reports based on category
  const filteredReports = useMemo(() => {
    if (!reports) return null;

    if (category === 'submission') {
      return reports.filter(r => SUBMISSION_REPORTS.has(r.id));
    } else if (category === 'agreement') {
      return reports.filter(r => AGREEMENT_REPORTS.has(r.id));
    }
    return reports; // show all if no category
  }, [reports, category]);

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Reports
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ alignItems: "flex-start" }}>
        {/* Left — selector */}
        <Paper sx={{ p: 2, width: { xs: "100%", md: 280 }, flexShrink: 0 }} elevation={2}>
          {reportsError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {reportsError}
            </Alert>
          )}
          {!reportsError && filteredReports === null && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {filteredReports && (
            <Stack spacing={1}>
              {filteredReports.map((r) => (
                <Button
                  key={r.id}
                  variant={r.id === activeId ? "contained" : "outlined"}
                  size="medium"
                  onClick={() => selectReport(r)}
                  sx={{ justifyContent: "flex-start", py: 1 }}
                >
                  {r.title}
                </Button>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Right — preview */}
        <Paper sx={{ flex: 1, minWidth: 0, p: 2 }} elevation={2}>
          {!activeId && (
            <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
              <Typography variant="body2">Select a report on the left to preview.</Typography>
            </Box>
          )}

          {activeId && (
            <>
              <Stack direction="row" sx={{ alignItems: "center", mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {activeTitle}
                  </Typography>
                  {data && (
                    <Typography variant="caption" color="text.secondary">
                      {isFilterable
                        ? `${exportableCount} of ${sortedRows.length} visible row${sortedRows.length === 1 ? "" : "s"} selected for export (${data.rows.length} total)`
                        : `${data.rows.length} row${data.rows.length === 1 ? "" : "s"}`}
                    </Typography>
                  )}
                </Box>
                <Tooltip
                  title={
                    isFilterable && exportableCount === 0
                      ? "Check at least one visible row to export"
                      : "Export as CSV"
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      onClick={downloadCsv}
                      disabled={!data || (isFilterable && exportableCount === 0)}
                      color="primary"
                      aria-label="Export CSV"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              {dataError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {dataError}
                </Alert>
              )}

              {dataLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              )}

              {data && data.rows.length === 0 && !dataLoading && (
                <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                  <Typography variant="body2">No rows.</Typography>
                </Box>
              )}

              {data && data.rows.length > 0 && isFilterable && agreementNameColumnIndex >= 0 && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                  {YEAR_FILTER_REPORT_IDS.has(activeId) && (
                    <Autocomplete
                      options={serverYearOptions}
                      getOptionLabel={(y) => String(y)}
                      value={serverYear}
                      onChange={(_, v) => {
                        setServerYear(v);
                        setPage(0);
                        loadYearFilteredData(activeId, v);
                      }}
                      sx={{ minWidth: 160, flex: 0 }}
                      renderInput={(params) => <TextField {...params} label="Year" size="small" />}
                    />
                  )}
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    options={agreementFilterOptions}
                    value={agreementFilterValue}
                    onChange={(_, next) => {
                      if (next.includes(SELECT_ALL_OPTION)) {
                        // Toggle: if the whole list is currently selected, clear to
                        // an empty explicit filter; otherwise clear the filter (null
                        // = all rows pass).
                        setSelectedAgreementNames(agreementAllSelected ? [] : null);
                      } else if (next.length === allAgreementNames.length) {
                        // User re-checked the last individual option — collapse to
                        // the canonical "no filter" state.
                        setSelectedAgreementNames(null);
                      } else {
                        setSelectedAgreementNames(next);
                      }
                      setPage(0);
                    }}
                    // Chips would overwhelm the input at dozens of agencies; the
                    // dropdown still shows which options are checked when opened.
                    renderTags={() => null}
                    renderOption={(props, option, { selected }) => {
                      const { key, ...rest } = props;
                      const isSelectAll = option === SELECT_ALL_OPTION;
                      return (
                        <li key={key} {...rest}>
                          <Checkbox
                            checked={isSelectAll ? agreementAllSelected : selected}
                            indeterminate={isSelectAll && agreementSomeSelected}
                            sx={{ mr: 1, p: 0.5 }}
                          />
                          {isSelectAll ? <strong>Select all</strong> : option}
                        </li>
                      );
                    }}
                    renderInput={(params) => <TextField {...params} label="Filter by Agreement_Name" />}
                    sx={{ flex: 1 }}
                  />
                  {serverYear && (
                    <Button
                      onClick={() => {
                        setServerYear(null);
                        setPage(0);
                        loadYearFilteredData(activeId, null);
                      }}
                      variant="outlined"
                      size="small"
                    >
                      Clear year filter
                    </Button>
                  )}
                </Stack>
              )}
              {data && data.rows.length > 0 && isSubmissionReport && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      limitTags={1}
                      options={yearOptions}
                      value={yearFilters}
                      onChange={(_, v) => {
                        setYearFilters(v);
                        setPage(0);
                      }}
                      sx={{ minWidth: 200, flex: 0 }}
                      renderInput={(params) => <TextField {...params} label="Year" size="small" />}
                    />
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      limitTags={1}
                      options={agencyOptions}
                      value={agencyFilters}
                      onChange={(_, v) => {
                        setAgencyFilters(v);
                        setPage(0);
                      }}
                      sx={{ minWidth: 320, flex: 1 }}
                      renderInput={(params) => <TextField {...params} label="Agency/Group" size="small" />}
                    />
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      limitTags={1}
                      options={subagencyOptions}
                      value={subagencyFilters}
                      onChange={(_, v) => {
                        setSubagencyFilters(v);
                        setPage(0);
                      }}
                      sx={{ minWidth: 320, flex: 1 }}
                      renderInput={(params) => <TextField {...params} label="Subagency" size="small" />}
                    />
                    {(yearFilters.length > 0 || agencyFilters.length > 0 || subagencyFilters.length > 0) && (
                      <Button
                        onClick={() => {
                          clearSubmissionFilters();
                          setPage(0);
                        }}
                        variant="outlined"
                        size="small"
                      >
                        Clear filters
                      </Button>
                    )}
                  </Stack>

                  {(yearFilters.length > 0 || agencyFilters.length > 0 || subagencyFilters.length > 0) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      {sortedRows.length} of {data.rows.length} row{data.rows.length === 1 ? "" : "s"} match filters
                    </Typography>
                  )}
                </>
              )}
              {data && data.rows.length > 0 && isSignerReport && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                    {YEAR_FILTER_REPORT_IDS.has(activeId) ? (
                      <Autocomplete
                        options={serverYearOptions}
                        getOptionLabel={(y) => String(y)}
                        value={serverYear}
                        onChange={(_, v) => {
                          setServerYear(v);
                          setPage(0);
                          loadYearFilteredData(activeId, v);
                        }}
                        sx={{ minWidth: 200, flex: 0 }}
                        renderInput={(params) => <TextField {...params} label="Year" size="small" />}
                      />
                    ) : (
                      signerColumns.date >= 0 && (
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          limitTags={1}
                          options={signerYearOptions}
                          value={signerYearFilters}
                          onChange={(_, v) => {
                            setSignerYearFilters(v);
                            setPage(0);
                          }}
                          sx={{ minWidth: 200, flex: 0 }}
                          renderInput={(params) => <TextField {...params} label="Year" size="small" />}
                        />
                      )
                    )}
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      limitTags={1}
                      options={signerAgencyOptions}
                      value={signerAgencyFilters}
                      onChange={(_, v) => {
                        setSignerAgencyFilters(v);
                        setPage(0);
                      }}
                      sx={{ minWidth: 320, flex: 1 }}
                      renderInput={(params) => <TextField {...params} label="Agency/Group" size="small" />}
                    />
                    {(signerYearFilters.length > 0 || signerAgencyFilters.length > 0 || serverYear) && (
                      <Button
                        onClick={() => {
                          clearSignerFilters();
                          if (YEAR_FILTER_REPORT_IDS.has(activeId)) {
                            setServerYear(null);
                            loadYearFilteredData(activeId, null);
                          }
                          setPage(0);
                        }}
                        variant="outlined"
                        size="small"
                      >
                        Clear filters
                      </Button>
                    )}
                  </Stack>

                  {(signerYearFilters.length > 0 || signerAgencyFilters.length > 0) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      {sortedRows.length} of {data.rows.length} row{data.rows.length === 1 ? "" : "s"} match filters
                    </Typography>
                  )}
                </>
              )}

              {data && sortedRows.length > 0 && (
                <>
                  <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
                    <Table stickyHeader size="small">
                      <TableHead
                        sx={{
                          "& .MuiTableCell-head": {
                            bgcolor: colors.darkGray,
                            color: "white",
                            fontWeight: 700,
                            whiteSpace: "nowrap"
                          }
                        }}
                      >
                        <TableRow>
                          {isFilterable && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                indeterminate={someVisibleChecked}
                                checked={allVisibleChecked}
                                onChange={toggleAll}
                                sx={{
                                  color: "white",
                                  "&.Mui-checked": { color: "white" },
                                  "&.MuiCheckbox-indeterminate": { color: "white" }
                                }}
                              />
                            </TableCell>
                          )}
                          {data.columns.map((c, idx) => {
                            const active = sortBy === idx;
                            return (
                              <TableCell key={c} sortDirection={active ? sortDir : false}>
                                <TableSortLabel
                                  active={active}
                                  direction={active ? sortDir : "asc"}
                                  onClick={() => {
                                    if (active) {
                                      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                                    } else {
                                      setSortBy(idx);
                                      setSortDir("asc");
                                    }
                                    setPage(0);
                                  }}
                                  sx={{
                                    color: "white !important",
                                    "&.Mui-active": { color: "white !important" },
                                    "& .MuiTableSortLabel-icon": { color: "white !important" }
                                  }}
                                >
                                  {c}
                                </TableSortLabel>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedRows.map(([row, originalIndex]) => (
                          <TableRow key={originalIndex} hover>
                            {isFilterable && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={checkedRows ? checkedRows.has(originalIndex) : false}
                                  onChange={() => toggleRow(originalIndex)}
                                />
                              </TableCell>
                            )}
                            {row.map((v, cIdx) => (
                              <TableCell key={cIdx} sx={{ whiteSpace: "nowrap" }}>
                                {formatCell(v)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    count={sortedRows.length}
                    rowsPerPage={rowsPerPage}
                    page={safePage}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </>
              )}
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}

// ISO datetime (with time component) or DateOnly "YYYY-MM-DD" — covers what the
// API serializes for DateTime/DateOnly columns. Plain numeric strings and other
// text fall through to String(v).
const dateValuePattern = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

function formatCell(v) {
  if (isEmpty(v)) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string" && dateValuePattern.test(v)) return fmtDateTime(v);
  // Long strings get truncated for display; the CSV export still has the full value.
  if (typeof v === "string" && v.length > 200) return v.slice(0, 200) + "…";
  return String(v);
}

// RFC 4180 CSV with a UTF-8 BOM so Excel auto-detects the encoding. CRLF line
// endings; only quote fields containing ", , CR, or LF; lowercase booleans; ISO
// datetimes reformatted to "yyyy-MM-dd HH:mm:ss".
function buildCsv(columns, rows) {
  const format = (v) => {
    if (isEmpty(v)) return "";
    if (typeof v === "boolean") return v ? "true" : "false";
    if (typeof v === "string" && dateValuePattern.test(v)) {
      // ISO "2024-01-15T14:30:00[.fff][Z|±HH:MM]" or "2024-01-15" → server form.
      const t = v.indexOf("T") >= 0 ? v.indexOf("T") : v.indexOf(" ");
      if (t < 0) return v; // DateOnly already "YYYY-MM-DD".
      const datePart = v.slice(0, t);
      const rest = v.slice(t + 1);
      // Strip fractional seconds and any trailing zone marker (Z or ±HH:MM).
      const timePart = rest.replace(/\.\d+/, "").replace(/(Z|[+-]\d{2}:?\d{2})$/, "");
      // Server format is HH:mm:ss; pad if the source omitted seconds.
      const paddedTime = timePart.length === 5 ? `${timePart}:00` : timePart;
      return `${datePart} ${paddedTime}`;
    }
    return String(v);
  };
  const escape = (v) => {
    const s = format(v);
    return s.length && /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map(escape).join(",")];
  for (const row of rows) lines.push(row.map(escape).join(","));
  return "﻿" + lines.join("\r\n") + "\r\n";
}
